export const LETTER_PAGE_WIDTH_PX = 794;
export const LETTER_PAGE_HEIGHT_PX = 1122;
export const SIGNATURE_EDITOR_CANVAS_WIDTH_PX = 149;
export const SIGNATURE_EDITOR_CANVAS_HEIGHT_PX = 119;
export const SIGNATURE_PREVIEW_WIDTH_PX = 149;
export const SIGNATURE_INNER_PADDING_PX = 0;
export const SIGNATURE_RENDER_PADDING_PX = 8;
const DEFAULT_SIGNATURE_WIDTH_PX = 149;
const DEFAULT_SIGNATURE_HEIGHT_PX = 119;

export interface DraftSignaturePlacement {
  pageIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
  leftPct?: number;
  topPct?: number;
  widthPct?: number;
  heightPct?: number;
}

export interface SignatureElementModel {
  src?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  rotation?: number;
  opacity?: number;
  zIndex?: number;
}

export interface SignatureHtmlModel {
  documentPadding?: number;
  textHtml?: string;
  textFontFamily?: string;
  textFontSize?: number;
  signature?: SignatureElementModel;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const roundPct = (value: number) => Number(value.toFixed(4));

export const withRelativePlacement = (
  placement: Omit<DraftSignaturePlacement, 'leftPct' | 'topPct' | 'widthPct' | 'heightPct'>,
  pageWidth = LETTER_PAGE_WIDTH_PX,
  pageHeight = LETTER_PAGE_HEIGHT_PX,
): DraftSignaturePlacement => ({
  ...placement,
  leftPct: roundPct((placement.x / Math.max(pageWidth, 1)) * 100),
  topPct: roundPct((placement.y / Math.max(pageHeight, 1)) * 100),
  widthPct: roundPct((placement.width / Math.max(pageWidth, 1)) * 100),
  heightPct: roundPct((placement.height / Math.max(pageHeight, 1)) * 100),
});

export const sanitizeDraftSignaturePlacement = (
  input?: Partial<DraftSignaturePlacement> | null,
  fallback?: Omit<DraftSignaturePlacement, 'leftPct' | 'topPct' | 'widthPct' | 'heightPct'>,
  pageWidth = LETTER_PAGE_WIDTH_PX,
  pageHeight = LETTER_PAGE_HEIGHT_PX,
): DraftSignaturePlacement => {
  const defaultPlacement = fallback || {
    pageIndex: 0,
    x: 56,
    y: 850,
    width: 168,
    height: 140,
  };

  const leftPct = Number(input?.leftPct);
  const topPct = Number(input?.topPct);
  const widthPct = Number(input?.widthPct);
  const heightPct = Number(input?.heightPct);

  const x = Number.isFinite(leftPct)
    ? Math.round((clamp(leftPct, 0, 100) / 100) * pageWidth)
    : Math.round(Number(input?.x) || defaultPlacement.x);
  const y = Number.isFinite(topPct)
    ? Math.round((clamp(topPct, 0, 100) / 100) * pageHeight)
    : Math.round(Number(input?.y) || defaultPlacement.y);
  const width = Number.isFinite(widthPct)
    ? Math.round((clamp(widthPct, 0.1, 100) / 100) * pageWidth)
    : Math.round(Number(input?.width) || defaultPlacement.width);
  const height = Number.isFinite(heightPct)
    ? Math.round((clamp(heightPct, 0.1, 100) / 100) * pageHeight)
    : Math.round(Number(input?.height) || defaultPlacement.height);

  return withRelativePlacement({
    pageIndex: Math.max(0, Math.round(Number(input?.pageIndex) || defaultPlacement.pageIndex)),
    x: Math.max(0, x),
    y: Math.max(0, y),
    width: Math.max(80, width),
    height: Math.max(64, height),
  }, pageWidth, pageHeight);
};

export const formatSignatureBoxLabel = (width: number, height: number) => `${Math.round(width)}×${Math.round(height)} px`;

export const hasVisibleSignatureContent = (html: string | null | undefined) => {
  if (!html || typeof window === 'undefined') return false;

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const hasImage = !!doc.querySelector('img[src]');
    const text = (doc.body.textContent || '').replace(/\u200B/g, '').trim();
    return hasImage || text.length > 0;
  } catch {
    return false;
  }
};

export const parseSignatureModelFromHtml = (html: string): SignatureHtmlModel | null => {
  if (!html || typeof window === 'undefined') return null;

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const marker = doc.querySelector('[data-signature-model]');
    const raw = marker?.getAttribute('data-signature-model');
    return raw ? JSON.parse(decodeURIComponent(raw)) : null;
  } catch {
    return null;
  }
};

const getModelImageBounds = (model: SignatureHtmlModel | null) => {
  const signature = model?.signature;
  if (!signature?.src) {
    return { width: DEFAULT_SIGNATURE_WIDTH_PX, height: DEFAULT_SIGNATURE_HEIGHT_PX };
  }

  const rightEdge = Math.max(200, Math.ceil((Number(signature.x) || 0) + (Number(signature.width) || 0)));
  const bottomEdge = Math.max(120, Math.ceil((Number(signature.y) || 0) + (Number(signature.height) || 0)));
  return {
    width: rightEdge,
    height: bottomEdge,
  };
};

export const getSignatureModelBox = (html: string) => {
  const model = parseSignatureModelFromHtml(html);
  const signature = model?.signature;
  if (!signature) return null;

  const x = Math.max(0, Number(signature.x) || 0);
  const y = Math.max(0, Number(signature.y) || 0);
  const width = Math.max(0, Number(signature.width) || 0);
  const height = Math.max(0, Number(signature.height) || 0);

  return {
    x,
    y,
    width,
    height,
    leftPct: roundPct((x / SIGNATURE_EDITOR_CANVAS_WIDTH_PX) * 100),
    topPct: roundPct((y / SIGNATURE_EDITOR_CANVAS_HEIGHT_PX) * 100),
    widthPct: roundPct((width / SIGNATURE_EDITOR_CANVAS_WIDTH_PX) * 100),
    heightPct: roundPct((height / SIGNATURE_EDITOR_CANVAS_HEIGHT_PX) * 100),
  };
};

export const normalizeSignatureHtmlForRender = (html: string | null | undefined) => {
  if (!html || typeof window === 'undefined') return html || '';

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const marker = doc.querySelector('[data-signature-model]');
    const root = marker?.nextElementSibling as HTMLElement | null;
    const model = marker?.getAttribute('data-signature-model')
      ? (JSON.parse(decodeURIComponent(String(marker.getAttribute('data-signature-model')))) as SignatureHtmlModel)
      : null;
    const imageBounds = getModelImageBounds(model);

    if (root) {
      root.style.position = 'relative';
      root.style.width = 'max-content';
      root.style.maxWidth = 'none';
      root.style.minWidth = `${imageBounds.width}px`;
      root.style.minHeight = `${imageBounds.height}px`;
      root.style.display = 'inline-block';
      root.style.overflow = 'visible';
      root.style.margin = '0';
      root.style.padding = '0';

      const textNode = root.querySelector('div[style*="line-height:1.5"]') as HTMLElement | null;
      if (textNode) {
        textNode.style.display = 'inline-block';
        textNode.style.width = 'max-content';
        textNode.style.maxWidth = 'none';
        textNode.style.minWidth = '0';
      }

      root.querySelectorAll('img').forEach((img) => {
        const image = img as HTMLElement;
        image.style.objectFit = 'contain';
        if (!image.style.width) image.style.width = 'auto';
        if (!image.style.height) image.style.height = 'auto';
        image.style.overflow = 'visible';
        image.style.transformOrigin = 'top left';
        image.style.boxSizing = 'border-box';
      });
    }

    return doc.body.innerHTML || html;
  } catch {
    return html;
  }
};

export const measureRenderedSignatureHtml = (html: string | null | undefined) => {
  if (!html || typeof window === 'undefined') {
    return { width: DEFAULT_SIGNATURE_WIDTH_PX, height: DEFAULT_SIGNATURE_HEIGHT_PX };
  }

  const normalizedHtml = normalizeSignatureHtmlForRender(html);
  const model = parseSignatureModelFromHtml(normalizedHtml || String(html));
  const imageBounds = getModelImageBounds(model);
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-99999px';
  host.style.top = '0';
  host.style.visibility = 'hidden';
  host.style.pointerEvents = 'none';
  host.style.overflow = 'visible';
  host.innerHTML = normalizedHtml;
  document.body.appendChild(host);

  try {
    const marker = host.querySelector('[data-signature-model]');
    const signatureRoot = (marker?.nextElementSibling as HTMLElement | null) || (host.firstElementChild as HTMLElement | null) || host;
    const rect = signatureRoot.getBoundingClientRect();
    const width = Math.max(
      DEFAULT_SIGNATURE_WIDTH_PX,
      Math.round(rect.width || 0),
      Math.round(signatureRoot.scrollWidth || 0),
      imageBounds.width,
    );
    const height = Math.max(
      DEFAULT_SIGNATURE_HEIGHT_PX,
      Math.round(rect.height || 0),
      Math.round(signatureRoot.scrollHeight || 0),
      imageBounds.height,
    );
    return { width, height };
  } finally {
    document.body.removeChild(host);
  }
};
